/**
 * OAuth consent page for the remote MCP transport.
 *
 * Reached when an MCP client (Claude Desktop's Custom Connectors,
 * Cursor's MCP, etc.) starts the OAuth authorization code flow on
 * `mcp.spotlight.help`.  The MCP server bounces the user here so
 * the page can:
 *
 *   1. Use the user's existing Clerk session — no second sign-in
 *      against an unfamiliar branded page.
 *   2. Let them pick which tenant + project the issued token will
 *      operate against, so a single Clerk identity that's a member
 *      of three tenants doesn't accidentally hand a tour-editor
 *      token to the wrong one.
 *   3. POST back to /oauth/consent with everything the backend
 *      needs to mint the underlying Spotlight API key + issue an
 *      OAuth authorization code.
 *
 * The page is deliberately spartan — security-relevant decisions
 * benefit from being legible, not slick.  The "Allow" button is
 * the only thing that submits anything.
 */

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { fetchMyTenants, resolveApiBase } from '../api.js';
import { XIcon, CheckIcon } from '../components/icons';

export function OAuthAuthorize() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [params] = useSearchParams();
  const txnId = params.get('txn_id') || '';
  const clientName = params.get('client_name') || 'An app';

  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState('');
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [scopeCatalog, setScopeCatalog] = useState(null);
  const [pickedScopes, setPickedScopes] = useState(null);  // null = not yet initialised
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Load the user's tenants once they're signed in.  The picker
  // surfaces only tenants where they have a membership, so the
  // backend's membership check on /oauth/consent never fires.
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    let alive = true;
    (async () => {
      try {
        const resp = await fetchMyTenants(getToken);
        if (!alive) return;
        const list = Array.isArray(resp) ? resp : (resp?.tenants || []);
        setTenants(list);
        if (list.length > 0 && !tenantId) {
          setTenantId(list[0].tenant_id);
        }
      } catch (e) {
        if (alive) setError(`Couldn't load your tenants: ${e.message}`);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [isLoaded, isSignedIn, getToken, tenantId]);

  // Load the scope catalog once.  Publicly readable.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const base = resolveApiBase();
        const r = await fetch(`${base}/oauth/scopes`);
        if (!r.ok) throw new Error(`scopes ${r.status}`);
        const body = await r.json();
        if (!alive) return;
        setScopeCatalog(body);
      } catch (e) {
        if (alive) setError(`Couldn't load scope catalog: ${e.message}`);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Pick the granting user's role on the currently-selected
  // tenant — drives which scopes are checkable.
  const currentRole = useMemo(() => {
    const t = tenants.find((x) => x.tenant_id === tenantId);
    return t?.role || '';
  }, [tenants, tenantId]);

  // Recompute available + default scopes whenever the role or the
  // catalog changes.  Default selected = intersection of the
  // catalog default + what the role can grant.
  useEffect(() => {
    if (!scopeCatalog || !currentRole) return;
    const available = new Set(scopeCatalog.role_available?.[currentRole] || []);
    const defaults = (scopeCatalog.default_selected || []).filter((s) => available.has(s));
    setPickedScopes(defaults);
  }, [scopeCatalog, currentRole]);

  function toggleScope(scope) {
    setPickedScopes((prev) => {
      const set = new Set(prev || []);
      if (set.has(scope)) set.delete(scope);
      else set.add(scope);
      return Array.from(set);
    });
  }

  // Load projects whenever the selected tenant changes.
  useEffect(() => {
    if (!tenantId) {
      setProjects([]);
      setProjectId('');
      return;
    }
    let alive = true;
    (async () => {
      try {
        const token = await getToken();
        const base = resolveApiBase();
        const r = await fetch(`${base}/v1/admin/projects`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Spotlight-Tenant-Id': tenantId,
          },
        });
        if (!r.ok) throw new Error(`projects ${r.status}`);
        const body = await r.json();
        const data = body?.data ?? body;
        const items = Array.isArray(data) ? data : (data?.items || []);
        if (!alive) return;
        setProjects(items);
        if (items.length > 0) {
          setProjectId(items[0].project_id || '');
        } else {
          setProjectId('');
        }
      } catch (e) {
        if (alive) setError(`Couldn't load projects: ${e.message}`);
      }
    })();
    return () => { alive = false; };
  }, [tenantId, getToken]);

  const requestedTenant = useMemo(
    () => tenants.find((t) => t.tenant_id === tenantId),
    [tenants, tenantId],
  );

  async function onAllow() {
    setSubmitting(true);
    setError(null);
    try {
      const token = await getToken();
      const base = resolveApiBase();
      const r = await fetch(`${base}/oauth/consent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          txn_id: txnId,
          tenant_id: tenantId,
          project_id: projectId || null,
          scopes: pickedScopes || [],
        }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({ detail: r.statusText }));
        throw new Error(body?.data?.detail || body?.detail || `consent failed (${r.status})`);
      }
      const body = await r.json();
      // Defend against the API envelope middleware wrapping the
      // response — the consent endpoint is excluded but a regression
      // would otherwise turn into a silent "browser stays here"
      // failure with no console error.
      const redirectUrl = body?.redirect_url ?? body?.data?.redirect_url;
      if (!redirectUrl) {
        throw new Error('No redirect_url in consent response — payload shape changed?');
      }
      window.location.assign(redirectUrl);
    } catch (e) {
      setError(e.message);
      setSubmitting(false);
    }
  }

  function onDeny() {
    // No backend round-trip — just close the tab or send them home.
    // Claude Desktop's redirect_uri is opaque to us at this point;
    // the cleanest UX is "this tab is done."
    window.close();
    // Some browsers don't allow programmatic close on tabs they
    // didn't open — fall back to a home redirect.
    setTimeout(() => { window.location.assign('/'); }, 200);
  }

  if (!isLoaded) return <p style={{ padding: 40 }}>Loading…</p>;
  if (!isSignedIn) return <p style={{ padding: 40 }}>Please sign in first.</p>;
  if (!txnId) return <p style={{ padding: 40 }}>Missing txn_id — this URL was reached without an active authorization request.</p>;

  return (
    <div className="oauth-page">
      <style>{OAUTH_CSS}</style>
      <main className="oauth-card">
        <h1 className="oauth-title">
          Allow <strong>{clientName}</strong> to access Spotlight?
        </h1>
        <p className="oauth-blurb">
          A token will be minted on the project you select below.  The
          app will be able to read and write tours, content, themes,
          and other admin resources within that project.  You can
          revoke it any time from the project's <strong>API Keys</strong> page.
        </p>

        {loading ? <p>Loading your tenants…</p> : (
          <>
            <label className="oauth-field">
              <span className="oauth-field__label">Tenant</span>
              <select
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                disabled={submitting}
              >
                {tenants.map((t) => (
                  <option key={t.tenant_id} value={t.tenant_id}>
                    {t.name} ({t.role})
                  </option>
                ))}
              </select>
            </label>

            <label className="oauth-field">
              <span className="oauth-field__label">Project</span>
              <select
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                disabled={submitting || projects.length === 0}
              >
                {projects.length === 0 ? (
                  <option value="">No projects yet — create one first</option>
                ) : (
                  projects.map((p) => (
                    <option key={p.project_id} value={p.project_id}>
                      {p.name || p.slug || p.project_id}
                    </option>
                  ))
                )}
              </select>
            </label>

            {scopeCatalog && pickedScopes !== null && (
              <fieldset className="oauth-scopes-grid">
                <legend>What can this app do?</legend>
                {scopeCatalog.scopes.map((s) => {
                  const allowedForRole = (scopeCatalog.role_available?.[currentRole] || []).includes(s.scope);
                  const checked = pickedScopes.includes(s.scope);
                  return (
                    <label
                      key={s.scope}
                      className={`oauth-scope-row${allowedForRole ? '' : ' oauth-scope-row--disabled'}`}
                      title={allowedForRole ? '' : `Your role (${currentRole || 'unknown'}) can't grant this scope`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!allowedForRole || submitting}
                        onChange={() => toggleScope(s.scope)}
                      />
                      <div>
                        <strong>{s.label}</strong>
                        <span className="oauth-scope-row__detail">
                          <code>{s.scope}</code> — {s.description}
                        </span>
                      </div>
                    </label>
                  );
                })}
              </fieldset>
            )}

            {error && <p className="oauth-error">{error}</p>}

            <div className="oauth-actions">
              <button
                className="btn btn--ghost"
                onClick={onDeny}
                disabled={submitting}
              >
                <XIcon /> Cancel
              </button>
              <button
                className="btn btn--primary"
                onClick={onAllow}
                disabled={submitting || !tenantId || projects.length === 0}
              >
                <CheckIcon /> {submitting ? 'Authorizing…' : `Allow ${requestedTenant?.name || 'this app'}`}
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

const OAUTH_CSS = `
  .oauth-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--surface-1, #f6f7fb);
    padding: 24px;
  }
  .oauth-card {
    max-width: 520px;
    width: 100%;
    background: var(--surface-0, #fff);
    border: 1px solid var(--border-1, #e5e7eb);
    border-radius: 16px;
    padding: 32px 36px;
    box-shadow: 0 4px 24px rgba(0,0,0,0.04);
  }
  .oauth-title {
    margin: 0 0 8px;
    font-size: 22px;
    line-height: 1.3;
  }
  .oauth-blurb {
    color: var(--text-2, #475569);
    font-size: 14.5px;
    line-height: 1.55;
    margin: 0 0 24px;
  }
  .oauth-field {
    display: block;
    margin-bottom: 14px;
  }
  .oauth-field__label {
    display: block;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-2, #475569);
    margin-bottom: 4px;
    font-weight: 600;
  }
  .oauth-field select {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid var(--border-1, #e5e7eb);
    border-radius: 8px;
    font-size: 14px;
    background: var(--surface-0, #fff);
  }
  .oauth-scopes-grid {
    margin: 18px 0 6px;
    padding: 14px 16px 10px;
    border: 1px solid var(--border-1, #e5e7eb);
    border-radius: 10px;
  }
  .oauth-scopes-grid legend {
    padding: 0 6px;
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-2, #475569);
    font-weight: 600;
  }
  .oauth-scope-row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 8px 4px;
    border-bottom: 1px solid var(--border-2, #f1f5f9);
    cursor: pointer;
  }
  .oauth-scope-row:last-child { border-bottom: none; }
  .oauth-scope-row input[type="checkbox"] { margin-top: 4px; }
  .oauth-scope-row__detail {
    display: block;
    color: var(--text-2, #475569);
    font-size: 12.5px;
    margin-top: 2px;
  }
  .oauth-scope-row__detail code {
    font-size: 11.5px;
    background: var(--surface-2, #f1f5f9);
    padding: 1px 5px;
    border-radius: 3px;
  }
  .oauth-scope-row--disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
  .oauth-error {
    color: var(--danger-700, #b91c1c);
    font-size: 13px;
    margin: 8px 0;
  }
  .oauth-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 20px;
  }
`;
